import { assign } from 'lodash-es'

import { Team } from './team.model'
import { Client } from './client.model'

export class BillingQuantities {
  teamId: number
  activeVehicleTrackers: number
  deactivatedVehicleTrackers: number
  activePersonalTrackers: number
  deactivatedPersonalTrackers: number
  activeAssetTrackers: number
  deactivatedAssetTrackers: number
  activeSatelliteTrackers: number
  deactivatedSatelliteTrackers: number
  activeVehicles: number
  archivedVehicles: number
  deletedVehicles: number
  activeSensors: number
  archivedSensors: number
  deletedSensors: number

  // Mock data
  // prismFTC: number

  constructor(src?: BillingQuantities) {
    src && assign(this, src)
  }

  get activeTrackers(): number {
    return this.activeVehicleTrackers + this.activePersonalTrackers + this.activeAssetTrackers + this.activeSatelliteTrackers
  }

  get deactivatedTrackers(): number {
    return (
      this.deactivatedVehicleTrackers + this.deactivatedPersonalTrackers + this.deactivatedAssetTrackers + this.deactivatedSatelliteTrackers
    )
  }
}

export class BillingPricing {
  id: number
  name: string
  deviceVehicleActive: number | null
  deviceVehicleDeactivated: number | null
  devicePersonalActive: number | null
  devicePersonalDeactivated: number | null
  deviceAssetActive: number | null
  deviceAssetDeactivated: number | null
  deviceSatelliteActive: number | null
  deviceSatelliteDeactivated: number | null
  vehicleVirtual: number | null
  tempSensor: number | null
  vehicleArchived: number | null
  sensorArchived: number | null
  status: string

  // Mock data
  // prismFTC: number | null

  constructor(src?: BillingPricing) {
    src && assign(this, src)
  }

  get activeTrackers(): number {
    return this.deviceVehicleActive + this.devicePersonalActive + this.deviceAssetActive + this.deviceSatelliteActive
  }

  get deactivatedTrackers(): number {
    return this.deviceVehicleDeactivated + this.devicePersonalDeactivated + this.deviceAssetDeactivated + this.deviceSatelliteDeactivated
  }
}

export enum BillingQuantitiesFields {
  activeVehicleTrackers = 'activeVehicleTrackers',
  deactivatedVehicleTrackers = 'deactivatedVehicleTrackers',
  activePersonalTrackers = 'activePersonalTrackers',
  deactivatedPersonalTrackers = 'deactivatedPersonalTrackers',
  activeAssetTrackers = 'activeAssetTrackers',
  deactivatedAssetTrackers = 'deactivatedAssetTrackers',
  activeSatelliteTrackers = 'activeSatelliteTrackers',
  deactivatedSatelliteTrackers = 'deactivatedSatelliteTrackers',
  activeVehicles = 'activeVehicles',
  virtualVehicles = 'virtualVehicles',
  archivedVehicles = 'archivedVehicles',
  deletedVehicles = 'deletedVehicles',
  activeSensors = 'activeSensors',
  archivedSensors = 'archivedSensors',
  deletedSensors = 'deletedSensors',

  // Mock data
  // prismFTC = 'prismFTC',
}

export enum BillingPricingFields {
  activeVehicleTrackers = 'deviceVehicleActive',
  deactivatedVehicleTrackers = 'deviceVehicleDeactivated',
  activePersonalTrackers = 'devicePersonalActive',
  deactivatedPersonalTrackers = 'devicePersonalDeactivated',
  activeAssetTrackers = 'deviceAssetActive',
  deactivatedAssetTrackers = 'deviceAssetDeactivated',
  activeSatelliteTrackers = 'deviceSatelliteActive',
  deactivatedSatelliteTrackers = 'deviceSatelliteDeactivated',
  virtualVehicles = 'vehicleVirtual',
  archivedVehicles = 'vehicleArchived',
  activeSensors = 'tempSensor',
  archivedSensors = 'sensorArchived',

  // Mock data
  // prismFTC = 'prismFTC',
}

export interface StripeCardKeys {
  clientSecret: string
  publicKey: string
}

export type PaymentMethodType = 'card' | 'au_becs_debit' | 'manual'

export interface PaymentMethods {
  card: Card[]
  auBecsDebit: Becs[]
  manual: ManualPaymentMethod | null
}

export interface ManualPaymentMethod {
  id: string
  type: PaymentMethodType
  is_default: boolean
}

export interface Card {
  brand: string
  exp_month: number
  exp_year: number
  id: string
  is_default: boolean
  last4: string
  name: string
  type: PaymentMethodType
}

export interface BecsMandate {
  status: string
  url: string
}

export interface Becs {
  bsb_number: string
  id: string
  is_default: boolean
  last4: string
  name: string
  email: string
  mandate: BecsMandate
  type: PaymentMethodType
}

export interface Invoice {
  id: string
  internalInvoiceId: string
  status: string
  periodStart: string
  periodEnd: string
  createdAt: string
  dueAt: string
  amount: string
  extInvoiceId?: number | string | null
  paymentFee: number
  tax: number
  totalTaxWithPrepayment: number
  totalAmountWithPrepayment: number
  prepaymentAmount: number
  previousPrepaymentAmount: number
  totalAmount: number
  details: InvoiceItems
}

interface InvoiceItems {
  active_vehicle_trackers: InvoiceItem
  deactivated_vehicle_trackers: InvoiceItem
  active_personal_trackers: InvoiceItem
  deactivated_personal_trackers: InvoiceItem
  active_asset_trackers: InvoiceItem
  deactivated_asset_trackers: InvoiceItem
  active_vehicles: InvoiceItem
  virtual_vehicles: InvoiceItem
  archived_vehicles: InvoiceItem
  active_sensors: InvoiceItem
  archived_sensors: InvoiceItem
  prepaymentData: PrepaymentData
}

interface PrepaymentData {
  previousPrepayment: InvoiceItem
  prepayment: InvoiceItem
}

interface InvoiceItem {
  key: string
  quantity: string
  price: string
  total: string
}

export enum InvoiceStatuses {
  Paid = 'paid',
  NotPaid = 'not_paid',
  Overdue = 'overdue',
  Processing = 'payment_processing',
  PaymentError = 'payment_error',
}

export const InvoiceStatusesLabel = {
  [InvoiceStatuses.Paid]: 'BILLING.CLIENT.STATUSES.PAID',
  [InvoiceStatuses.NotPaid]: 'BILLING.CLIENT.STATUSES.NOT_PAID',
  [InvoiceStatuses.Processing]: 'BILLING.CLIENT.STATUSES.PROCESSING',
  [InvoiceStatuses.Overdue]: 'BILLING.CLIENT.STATUSES.OVERDUE',
  [InvoiceStatuses.PaymentError]: 'BILLING.CLIENT.STATUSES.PAYMENT_ERROR',
}

export interface AccountStatus {
  invoicesMessage: string
  paymentMessage: string
  type: AccountStatusType
}

export type AccountStatusType = 'good' | 'warning' | 'error'

export interface BillingPlan {
  displayName: string
  id: number
  name: string
}

export interface BillingSummary {
  accountStatus: AccountStatus
  billingPlan: BillingPlan
  //defaultPaymentMethod: Card | Becs
  subscriptionPrice: number
}

export interface AdminInfo {
  companyName: string
  legalName: string
  abn: string
  legalAddress: string
  billingAddress: string
}
export interface XeroCredentials {
  xeroClientId?: string
  xeroClientSecret?: string
  teamId?: number
  xeroAccountLineitemId?: string
  xeroAccountPaymentId?: string
}

export interface XeroAccount {
  class: string
  code: string
  description: string
  name: string
}

export interface XeroContact {
  contactId: string
  name: string
  client: Pick<Client, 'id' | 'name'> | null
  hidden?: boolean
  disabled?: boolean
}

export interface XeroClientContact {
  id: number
  client: Client
  team: Team
  xeroContactId: string
}

export interface XeroGetContactsParams {
  where?: string
  order?: string
  IDs?: string[]
  page?: number
  includeArchived?: boolean
  summaryOnly?: boolean
  searchTerm?: string
}

export interface StripeKeys {
  secretKey: string
  publicKey: string
}

export interface BankAccount {
  accountName?: string
  bsb?: string
  accountNumber?: string
  swiftCode?: string
  isStripeEnabled?: boolean
}
